import apiService from './api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants';

/**
 * Authentication Service
 * Handles all authentication-related API calls and localStorage management
 */
class AuthService {
  /**
   * Register a new user
   * @param {object} userData - User registration data
   * @returns {Promise} - Response data
   */
  async register(userData) {
    const response = await apiService.post(API_ENDPOINTS.AUTH.REGISTER, userData, {
      includeAuth: false,
    });
    return response;
  }

  /**
   * Login user
   * @param {object} credentials - Email and password
   * @returns {Promise} - Response data with user and token
   */
  async login(credentials) {
    const response = await apiService.post(API_ENDPOINTS.AUTH.LOGIN, credentials, {
      includeAuth: false,
    });

    // Store token and user in localStorage
    if (response.token) {
      this.setToken(response.token);
    }

    if (response.user) {
      this.setCurrentUser(response.user);
    }

    return response;
  }

  /**
   * Logout user
   * Clears all auth data from localStorage
   */
  logout() {
    this.removeToken();
    this.removeCurrentUser();
  }

  /**
   * Get current user profile from API
   * @returns {Promise} - User profile data
   */
  async getProfile() {
    const response = await apiService.get(API_ENDPOINTS.AUTH.PROFILE);
    return response;
  }

  /**
   * Update user profile
   * @param {object} updates - Profile updates
   * @returns {Promise} - Updated user data
   */
  async updateProfile(updates) {
    const response = await apiService.put(API_ENDPOINTS.AUTH.PROFILE, updates);

    // Update user in localStorage
    if (response.user || response) {
      const updatedUser = response.user || response;
      this.setCurrentUser(updatedUser);
    }

    return response;
  }

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise} - Response data
   */
  async verifyEmail(token) {
    const response = await apiService.get(`${API_ENDPOINTS.AUTH.VERIFY_EMAIL}/${token}`, {
      includeAuth: false,
    });
    return response;
  }

  // ============================================
  // LocalStorage Management Methods
  // ============================================

  /**
   * Get token from localStorage
   * @returns {string|null} - JWT token
   */
  getToken() {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Set token in localStorage
   * @param {string} token - JWT token
   */
  setToken(token) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  /**
   * Remove token from localStorage
   */
  removeToken() {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Get current user from localStorage
   * @returns {object|null} - User object
   */
  getCurrentUser() {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    if (user) {
      try {
        return JSON.parse(user);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Set current user in localStorage
   * @param {object} user - User object
   */
  setCurrentUser(user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  /**
   * Remove current user from localStorage
   */
  removeCurrentUser() {
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if token exists
   */
  isAuthenticated() {
    const token = this.getToken();
    return !!token;
  }

  /**
   * Get user role from localStorage
   * @returns {string|null} - User role
   */
  getUserRole() {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  /**
   * Check if user is admin
   * @returns {boolean} - True if user is admin
   */
  isAdmin() {
    const role = this.getUserRole();
    return role === 'admin';
  }

  /**
   * Check if user is moderator
   * @returns {boolean} - True if user is moderator
   */
  isModerator() {
    const role = this.getUserRole();
    return role === 'moderator';
  }

  /**
   * Check if user has admin or moderator role
   * @returns {boolean} - True if user is admin or moderator
   */
  hasAdminAccess() {
    const role = this.getUserRole();
    return role === 'admin' || role === 'moderator';
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
